import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { UploadIcon, LockIcon, LogOutIcon, ImagePlusIcon, Loader2Icon, CheckCircleIcon, Trash2Icon } from 'lucide-react';
type CustomPhoto = {
  src: string;
  alt: string;
  year: string;
};
export function SettingsPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('fch_admin_logged_in') === 'true');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  // Cloudinary settings
  const [cloudName, setCloudName] = useState(localStorage.getItem('fch_cloud_name') || '');
  const [uploadPreset, setUploadPreset] = useState(localStorage.getItem('fch_upload_preset') || '');
  const [selectedYear, setSelectedYear] = useState('2026');
  // Custom photos state
  const [customPhotos, setCustomPhotos] = useState<CustomPhoto[]>([]);
  const loadPhotos = () => {
    const saved = localStorage.getItem('fch_custom_photos');
    if (saved) {
      try {
        setCustomPhotos(JSON.parse(saved));
      } catch (e) {
        console.error('Chyba při načítání fotek', e);
      }
    } else {
      setCustomPhotos([]);
    }
  };
  // Load photos on mount and when login state changes
  useEffect(() => {
    if (isLoggedIn) {
      loadPhotos();
    }
  }, [isLoggedIn]);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === 'kasspi' && password === '2368') {
      setIsLoggedIn(true);
      localStorage.setItem('fch_admin_logged_in', 'true');
      setError('');
    } else {
      setError('Nesprávné přihlašovací údaje.');
    }
  };
  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('fch_admin_logged_in');
    localStorage.removeItem('fch_global_authorized');
    setUsername('');
    setPassword('');
    window.location.href = '/';
  };
  const handleSaveSettings = () => {
    localStorage.setItem('fch_cloud_name', cloudName);
    localStorage.setItem('fch_upload_preset', uploadPreset);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 3000);
  };
  const uploadToCloudinary = async (file: File) => {
    if (!cloudName || !uploadPreset) {
      alert('Prosím vyplňte Cloud Name a Upload Preset.');
      return;
    }
    setIsUploading(true);
    setUploadSuccess(false);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset);
    try {
      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.secure_url) {
        // Save to localStorage for the Gallery page to pick up
        const existingPhotos = JSON.parse(localStorage.getItem('fch_custom_photos') || '[]');
        const newPhoto = {
          src: data.secure_url,
          alt: 'Nahraná fotografie',
          year: selectedYear
        };
        const updatedPhotos = [...existingPhotos, newPhoto];
        localStorage.setItem('fch_custom_photos', JSON.stringify(updatedPhotos));
        setCustomPhotos(updatedPhotos);
        // Save settings
        localStorage.setItem('fch_cloud_name', cloudName);
        localStorage.setItem('fch_upload_preset', uploadPreset);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);
      } else {
        throw new Error(data.error?.message || 'Chyba při nahrávání');
      }
    } catch (err) {
      alert('Nahrávání selhalo: ' + (err as Error).message);
    } finally {
      setIsUploading(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      uploadToCloudinary(file);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadToCloudinary(file);
    }
  };
  const handleDeletePhoto = (index: number) => {
    if (!window.confirm('Opravdu chcete tuto fotografii smazat z galerie?')) return;
    const updatedPhotos = customPhotos.filter((_, i) => i !== index);
    localStorage.setItem('fch_custom_photos', JSON.stringify(updatedPhotos));
    setCustomPhotos(updatedPhotos);
  };
  if (!isLoggedIn) {
    return <main className="pt-32 pb-24 min-h-screen bg-off-white flex items-center justify-center px-6" data-id="element-311" data-mp="1qbq8m5m0r7b6">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full" data-id="element-312" data-mp="258za3s51drgm">
          <div className="flex justify-center mb-6" data-id="element-313" data-mp="2ju3ea255gho3">
            <div className="bg-mist p-4 rounded-full text-blue-medium" data-id="element-314" data-mp="2nwrpuhl902or">
              <LockIcon className="w-8 h-8" data-id="element-315" data-mp="29ya4urmq94aj" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-navy-deep text-center mb-8" data-id="element-316" data-mp="2a0e6t412ft86">
            Přihlášení do administrace
          </h1>

          <form onSubmit={handleLogin} className="space-y-6" data-id="element-317" data-mp="207ypm8xlf8oc">
            <div data-id="element-318" data-mp="206duoi7d9npy">
              <label className="block text-sm font-medium text-navy-deep/80 mb-2" data-id="element-319" data-mp="24yoy3v1bfrn9">
                Uživatelské jméno
              </label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-mist focus:border-blue-medium focus:ring-2 focus:ring-blue-medium/20 outline-none transition-all" placeholder="Zadejte jméno" data-id="element-320" data-mp="1r8tqb0e7jbb4" />
            </div>
            <div data-id="element-321" data-mp="134vumh5etra3">
              <label className="block text-sm font-medium text-navy-deep/80 mb-2" data-id="element-322" data-mp="135dox88ihvfd">
                Heslo
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-mist focus:border-blue-medium focus:ring-2 focus:ring-blue-medium/20 outline-none transition-all" placeholder="Zadejte heslo" data-id="element-323" data-mp="yb9g0jfordbn" />
            </div>

            {error && <p className="text-red-500 text-sm text-center" data-id="element-324" data-mp="1c48xgqz13hlu">{error}</p>}

            <button type="submit" className="w-full bg-blue-medium text-white py-3 rounded-lg font-medium hover:bg-blue-sky transition-colors" data-id="element-325" data-mp="175fu3rdgh6ze">
              Přihlásit se
            </button>
          </form>
        </motion.div>
      </main>;
  }
  return <main className="pt-32 pb-24 min-h-screen bg-off-white" data-id="element-326" data-mp="27dma2qgt9e1d">
      <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-327" data-mp="27f74mg34kwz8">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-328" data-mp="2cc5v8onkylso">
          <div className="flex justify-between items-center mb-12 border-b border-mist pb-6" data-id="element-329" data-mp="2m2ihclb1mn0x">
            <h1 className="text-3xl font-light text-navy-deep" data-id="element-330" data-mp="2r1lj9wvq8d4o">
              Správa galerie
            </h1>
            <button onClick={handleLogout} className="flex items-center gap-2 text-navy-deep/60 hover:text-red-500 transition-colors" data-id="element-331" data-mp="2v2x3fmvu065h">
              <LogOutIcon className="w-5 h-5" data-id="element-332" data-mp="300grfeibzxam" />
              <span data-id="element-333" data-mp="350bh5eqdegu3">Odhlásit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-334" data-mp="15oiildnxp3f9">
            {/* Settings Sidebar */}
            <div className="bg-white rounded-2xl shadow-sm border border-mist p-6 h-fit" data-id="element-335" data-mp="1an3lxlyhg37x">
              <h2 className="text-lg font-semibold text-navy-deep mb-4" data-id="element-336" data-mp="3ckm3xj3d7e41">
                Nastavení Cloudinary
              </h2>
              <div className="space-y-4" data-id="element-337" data-mp="37krd1fjlaoiz">
                <div data-id="element-338" data-mp="37jpzn4f3ppy3">
                  <label className="block text-xs font-bold text-navy-deep/50 uppercase mb-1" data-id="element-339" data-mp="3map2rzx63maa">
                    Cloud Name
                  </label>
                  <input type="text" value={cloudName} onChange={e => setCloudName(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-mist text-sm outline-none focus:border-blue-medium" placeholder="Např. dxy12345" data-id="element-340" data-mp="3hc45o9d7piq2" />
                </div>
                <div data-id="element-341" data-mp="2yk2xuhigmi4i">
                  <label className="block text-xs font-bold text-navy-deep/50 uppercase mb-1" data-id="element-342" data-mp="2yjjgb1wq1vr0">
                    Upload Preset (Unsigned)
                  </label>
                  <input type="text" value={uploadPreset} onChange={e => setUploadPreset(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-mist text-sm outline-none focus:border-blue-medium" placeholder="Např. ml_default" data-id="element-343" data-mp="dvon65f4663g" />
                </div>
              </div>
              <button onClick={handleSaveSettings} className="w-full mt-4 bg-navy-deep text-white py-2 rounded-lg text-sm font-medium hover:bg-navy-deep/90 transition-all flex items-center justify-center gap-2" data-id="element-344" data-mp="gejpojxr90w2">
                {settingsSaved ? <>
                    <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-345" data-mp="vbsv2hjw856s" />
                    Uloženo
                  </> : 'Uložit nastavení'}
              </button>
            </div>

            {/* Upload Area */}
            <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-mist p-8" data-id="element-346" data-mp="1rv04cb1hjdhv">
              <h2 className="text-xl font-semibold text-navy-deep mb-6 flex items-center gap-3" data-id="element-347" data-mp="3b3ejk7nc9fls">
                <ImagePlusIcon className="w-6 h-6 text-blue-medium" data-id="element-348" data-mp="3g3khu6o460cu" />
                Nahrát nové fotografie
              </h2>

              <div className="mb-6" data-id="element-349" data-mp="314yv6cfp4kfz">
                <label className="block text-sm font-medium text-navy-deep/80 mb-2" data-id="element-350" data-mp="3184ibm4rlc92">
                  Vyberte ročník
                </label>
                <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="w-full max-w-xs px-4 py-3 rounded-lg border border-mist focus:border-blue-medium focus:ring-2 focus:ring-blue-medium/20 outline-none bg-white" data-id="element-351" data-mp="2sd6rckl6g9kd">
                  <option value="2026" data-id="element-352" data-mp="2nevdzzvobgxe">Ročník 2026</option>
                  <option value="2025" data-id="element-353" data-mp="29e2hj9ltabrn">Ročník 2025</option>
                </select>
              </div>

              <div onDragOver={e => {
              e.preventDefault();
              setIsDragging(true);
            }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop} className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-colors duration-200 ${isDragging ? 'border-blue-medium bg-blue-medium/5' : 'border-mist hover:border-blue-medium/50 hover:bg-off-white'} ${isUploading ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-354" data-mp="2dgpbph5yg8s0">
                <input type="file" onChange={handleFileSelect} className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" data-id="element-355" data-mp="2dowt0gxkvrab" />
                <div className="flex justify-center mb-4" data-id="element-356" data-mp="2ihg7dg43aqg6">
                  <div className="bg-mist p-4 rounded-full text-blue-medium" data-id="element-357" data-mp="2ihzqv19e1mwb">
                    {isUploading ? <Loader2Icon className="w-8 h-8 animate-spin" data-id="element-358" data-mp="2wf4vqxxd8fpz" /> : <UploadIcon className="w-8 h-8" data-id="element-359" data-mp="2rg1nz5q03veb" />}
                  </div>
                </div>
                <h3 className="text-lg font-medium text-navy-deep mb-2" data-id="element-360" data-mp="1t6bjp3eiaoo6">
                  {isUploading ? 'Nahrávám...' : 'Přetáhněte fotografie sem'}
                </h3>
                <p className="text-navy-deep/60 mb-6" data-id="element-361" data-mp="1y43nauoijz87">
                  nebo klikněte pro výběr souborů z počítače
                </p>

                {uploadSuccess && <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex items-center justify-center gap-2 text-green-600 font-medium" data-id="element-362" data-mp="27ug3kapap68j">
                    <CheckCircleIcon className="w-5 h-5" data-id="element-363" data-mp="miy6e6lsopt4" />
                    Hotovo! Fotka je v galerii.
                  </motion.div>}
              </div>
            </div>
          </div>

          {/* Uploaded photos management */}
          <div className="mt-8 bg-white rounded-2xl shadow-sm border border-mist p-8" data-id="element-364" data-mp="jz9zuyvkxjm2">
            <div className="flex justify-between items-center mb-6" data-id="element-365" data-mp="f4nm4p20zeyt">
              <h2 className="text-xl font-semibold text-navy-deep" data-id="element-366" data-mp="a8fb9nuv27xg">
                Nahrané fotografie ({customPhotos.length})
              </h2>
              <button onClick={loadPhotos} className="text-sm text-blue-medium hover:underline flex items-center gap-1" data-id="element-367" data-mp="12wb3af9hjbnw">
                <Loader2Icon className="w-4 h-4" data-id="element-368" data-mp="xy17c2fxs718" />
                Obnovit seznam
              </button>
            </div>
            {customPhotos.length === 0 ? <p className="text-navy-deep/50 text-sm" data-id="element-369" data-mp="ovi77q3jc8io">
                Zatím nebyly nahrány žádné fotografie.
              </p> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-370" data-mp="3dm9pkw56usj5">
                {customPhotos.map((photo, i) => <div key={`${photo.src}-${i}`} className="group relative aspect-square overflow-hidden rounded-xl border border-mist bg-mist" data-id="element-371" data-mp="11dklzgkgnbqh" data-mpi={i}>
                    <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" data-id="element-372" data-mp="rgobj5b04fja" />
                    <span className="absolute top-2 left-2 bg-navy-deep/80 text-white text-xs px-2 py-0.5 rounded-full" data-id="element-373" data-mp="1k5vj0tsfofgo">
                      {photo.year}
                    </span>
                    <button type="button" onClick={() => handleDeletePhoto(i)} className="absolute top-2 right-2 bg-white/90 text-red-500 p-1.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white transition-all" aria-label="Smazat fotografii" data-id="element-374" data-mp="1b4npsq4ry4tg">
                      <Trash2Icon className="w-4 h-4" data-id="element-375" data-mp="3sbnymv851rro" />
                    </button>
                  </div>)}
              </div>}
          </div>
        </motion.div>
      </div>
    </main>;
}